import React, { FC, useState, ChangeEvent, useEffect } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, TextField } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { ChatTemplate } from 'models/order';
import { useTranslation } from 'react-i18next';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { useMutation } from '@apollo/client/react';
import { sendChatMessageTemplate } from 'apollo/templates/message';
import { ApolloError } from '@apollo/client';
import { ChatMessage } from 'models/chat-messages';

type ChatTemplateDialogProps = {
    open: boolean;
    onClose: () => void;
    onSend: (error?: ApolloError, chatMessage?: ChatMessage) => void;
    orderId: string;
};

const ChatTemplateDialog: FC<ChatTemplateDialogProps> = ({
    open,
    onClose,
    onSend,
    orderId,
}: ChatTemplateDialogProps) => {
    const { t: translate } = useTranslation(['messages', 'common']);
    const [templateOptions, setTemplateOptions] = useState<ChatTemplate[]>([]);
    const [message, setMessage] = useState('');

    const [sendChatMessage] = useMutation(sendChatMessageTemplate, {
        onCompleted: ({ sendChatMessage }) => {
            onSend(undefined, sendChatMessage);
        },
        onError: (error: ApolloError) => {
            onSend(error);
        },
    });

    useEffect(() => {
        try {
            const currentOrganization = currentOrgVar();
            const chatTemplateOptions = customerSettings.getCustomerSetting(
                currentOrganization.customerSettings,
                CONSTANTS.SETTINGS_NAMES.CHAT_TEMPLATE,
            );
            setTemplateOptions(chatTemplateOptions);
        } catch (error) {
            onSend(new ApolloError({ errorMessage: translate('messages:ERROR_PARSING_SETTINGS') }));
        }
    }, []);

    const handleOptionChange = (event: SelectChangeEvent<ChatTemplate>) => {
        const message = event.target.value as string;
        setMessage(message);
    };

    const onMessageChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
    };

    const closeDialog = () => {
        setMessage('');
        onClose();
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="lg"
            open={open}
            onClose={closeDialog}
            title={translate('messages:CHAT_TEMPLATE')}
            actions={
                <Button
                    key="send"
                    onClick={() =>
                        sendChatMessage({
                            variables: {
                                orderId,
                                message,
                            },
                        })
                    }
                    data-test="chat-template-dialog.send-btn"
                >
                    {translate('messages:SEND')}
                </Button>
            }
        >
            <Box mt={2}>
                <FormControl fullWidth>
                    <InputLabel>{translate('messages:SELECT_TEMPLATE')}</InputLabel>
                    <Select
                        label={translate('messages:SELECT_TEMPLATE')}
                        onChange={handleOptionChange}
                        data-test="chat-template-dialog.select-template"
                    >
                        {templateOptions.map((option: ChatTemplate, index: number) => (
                            <MenuItem key={index} value={option.message}>
                                {option.title}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box mt={1}>
                    <TextField
                        value={message}
                        onChange={onMessageChange}
                        placeholder={translate('messages:TYPE_CHAT_MESSAGE')}
                        fullWidth
                        id="outlined-textarea"
                        multiline
                        rows={5}
                        data-test="chat-template-dialog.message-input"
                    />
                </Box>
            </Box>
        </DHDialog>
    );
};

export default ChatTemplateDialog;
